import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useCart } from '../context/CartContext';
import { useSession } from '../context/SessionContext';
import { secureStorage } from '../utils/security';
import AuthStep from '../components/checkout/AuthStep';
import CartStep from '../components/checkout/CartStep';
import AddressStep from '../components/checkout/AddressStep';
import CheckoutStep from '../components/checkout/CheckoutStep';
import UpiStep from '../components/checkout/UpiStep';
import CashfreePayStep from '../components/checkout/CashfreePayStep';
import RazorpayPayStep from '../components/checkout/RazorpayPayStep';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Steps: 'auth' -> 'cart' -> 'address' -> 'payment' -> 'upi' | 'cashfree' | 'razorpay'
// Ported from the old CheckoutModal.js almost verbatim — only the modal chrome
// (Overlay/Sheet/onClose-as-prop) changed to a routed page. Every handler,
// API call and state transition is unchanged.
export default function CheckoutPage() {
  const { lang, t } = useLang();
  const { cart, total, clearCart } = useCart();
  const { sessionUser, onAuth, phone, name, fromWhatsApp } = useSession();
  const { setSupportOpen } = useOutletContext();
  const navigate = useNavigate();
  const te = lang === 'te';
  const L = (en, teVal) => (te ? teVal : en);

  const authHeaders = () => {
    const token = sessionUser?.token || secureStorage.get('nrk_user')?.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const finishCheckout = (result, resolvedName, resolvedPhone) => {
    if (resolvedPhone && !sessionUser && result?.token) {
      onAuth({ phone: resolvedPhone, name: resolvedName || '', token: result.token });
    } else if (resolvedPhone && sessionUser && !sessionUser.token && result?.token) {
      onAuth({ ...sessionUser, token: result.token });
    }
    navigate('/', { state: { placedResult: result } });
  };

  // ── Auth state ────────────────────────────────────────────────
  const [authStep, setAuthStep] = useState('form');
  const [authName, setAuthName] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [consent, setConsent] = useState(true);
  const [authOtp, setAuthOtp] = useState(['', '', '', '', '', '']);
  const [normPhone, setNormPhone] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [resendLeft, setResendLeft] = useState(0);
  const otpRefs = useRef([]);

  // ── Checkout state ────────────────────────────────────────────
  const [step, setStep] = useState('auth');
  const [payMethod, setPayMethod] = useState('cod');
  const [nameVal, setNameVal] = useState('');
  const [phoneVal, setPhoneVal] = useState('');
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState(null);
  const [locState, setLocState] = useState('idle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [upiSettings, setUpiSettings] = useState({
    upi_enabled: false, upi_pa: '', upi_name: 'NRK Mart',
    cashfree_enabled: false, cashfree_env: 'sandbox',
    razorpay_enabled: false, razorpay_key_id: '', razorpay_env: 'test',
    online_payment_provider: 'off', otp_required: true,
  });
  const [orderResult, setOrderResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [settingsReady, setSettingsReady] = useState(false);
  const [deliveryType, setDeliveryType] = useState('normal');
  const [immediateCharge, setImmediateCharge] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [availablePromos, setAvailablePromos] = useState([]);
  const [pricingPreview, setPricingPreview] = useState(null);

  const loadAddresses = useCallback(async (acctPhone, token) => {
    if (!acctPhone) { setSavedAddresses([]); return; }
    try {
      const res = await axios.get(`${API}/api/me/addresses`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { phone: acctPhone },
      });
      const list = res.data.addresses || [];
      setSavedAddresses(list);
      const def = list.find((a) => a.is_default) || list[0];
      if (def) {
        setSelectedAddressId(def.id);
        setLocation({
          lat: def.lat, lng: def.lng,
          label: [def.label, def.house_no, def.line1, def.landmark, def.city, def.state, def.pincode].filter(Boolean).join(', '),
          address_id: def.id,
        });
        setLocState('got');
        if (def.name) setNameVal(def.name);
        if (def.phone_contact) {
          const digits = String(def.phone_contact).replace(/\D/g, '').slice(-10);
          if (digits.length === 10) setPhoneVal(digits);
        }
      }
    } catch {
      try {
        const r2 = await axios.get(`${API}/api/orders/track/${encodeURIComponent(acctPhone)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const seen = new Set(); const addrs = [];
        (r2.data.orders || []).forEach((o) => {
          if (o.delivery_label && !seen.has(o.delivery_label)) {
            seen.add(o.delivery_label);
            addrs.push({ label: o.delivery_label, lat: o.delivery_lat, lng: o.delivery_lng });
          }
        });
        setSavedAddresses(addrs);
      } catch { setSavedAddresses([]); }
    }
  }, []);

  const fetchPricingPreview = useCallback(async (opts = {}) => {
    if (!cart.length) { setPricingPreview(null); return null; }
    try {
      const walletAmt = (opts.payMethod || payMethod) === 'wallet' ? undefined : 0;
      const body = {
        items: cart.map((i) => ({ product_id: i.product_id, quantity_kg: i.quantity_kg })),
        promo_code: (opts.promoCode !== undefined ? opts.promoCode : (promoResult ? promoCode.trim().toUpperCase() : undefined)) || undefined,
        delivery_type: opts.deliveryType || deliveryType,
        wallet_amount: opts.walletAmount != null ? opts.walletAmount : walletAmt,
        phone: sessionUser?.phone || phone || undefined,
      };
      const res = await axios.post(`${API}/api/pricing/preview`, body, { headers: authHeaders() });
      let quote = res.data;
      if ((opts.payMethod || payMethod) === 'wallet') {
        const res2 = await axios.post(`${API}/api/pricing/preview`, { ...body, wallet_amount: quote.total_amount }, { headers: authHeaders() });
        quote = res2.data;
      }
      setPricingPreview(quote);
      return quote;
    } catch (err) {
      if (opts.rethrow) throw err;
      setPricingPreview(null);
      return null;
    }
  }, [cart, payMethod, promoResult, promoCode, deliveryType, sessionUser, phone]);

  // ── On mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!cart.length && !sessionStorage.getItem('nrk_rzp_pending') && !sessionStorage.getItem('nrk_cf_pending')) {
      navigate('/cart', { replace: true });
      return;
    }
    axios.get(`${API}/api/auth/settings`).then(async (r) => {
      setUpiSettings(r.data);
      setImmediateCharge(parseFloat(r.data.immediate_delivery_charge) || 0);
      if (r.data.razorpay_enabled) setPayMethod('razorpay');
      else if (r.data.cashfree_enabled) setPayMethod('cashfree');
      else if (r.data.upi_enabled) setPayMethod('upi');
      else setPayMethod('cod');

      const acctPhone = sessionUser?.phone || phone;
      await loadAddresses(acctPhone, sessionUser?.token);

      const effectivePhone = sessionUser?.phone || phone;
      const effectiveName = sessionUser?.name || name;
      if (effectivePhone) {
        setPhoneVal(effectivePhone.replace(/^\+91/, '').replace(/\D/g, ''));
        if (effectiveName) setNameVal(effectiveName);
        setStep('cart');
        axios.get(`${API}/api/wallet/${encodeURIComponent(effectivePhone)}`, { headers: authHeaders() })
          .then((wr) => setWalletBalance(wr.data.balance || 0)).catch(() => {});
      } else {
        setStep('auth');
      }
      setSettingsReady(true);
    }).catch(() => {
      const effectivePhone = sessionUser?.phone || phone;
      const effectiveName = sessionUser?.name || name;
      if (effectivePhone) {
        setPhoneVal(effectivePhone.replace(/^\+91/, '').replace(/\D/g, ''));
        if (effectiveName) setNameVal(effectiveName);
        setStep('cart');
      } else {
        setStep('auth');
      }
      setSettingsReady(true);
    });
  }, []);

  useEffect(() => {
    if (step !== 'payment') return;
    fetchPricingPreview();
  }, [step, deliveryType, payMethod, promoResult, cart]);

  useEffect(() => {
    if (step !== 'payment') return undefined;
    let cancelled = false;
    const itemsTotal = cart.reduce((s, i) => s + (Number(i.subtotal) || 0), 0);
    const ph = sessionUser?.phone || (phoneVal ? `+91${String(phoneVal).replace(/\D/g, '').slice(-10)}` : '');
    axios.get(`${API}/api/promos/eligible`, { params: { total: itemsTotal, phone: ph || undefined }, headers: authHeaders() })
      .then((r) => { if (!cancelled) setAvailablePromos(r.data?.promos || []); })
      .catch(() => { if (!cancelled) setAvailablePromos([]); });
    return () => { cancelled = true; };
  }, [step, cart, promoResult, sessionUser?.phone, phoneVal]);

  useEffect(() => {
    if (resendLeft <= 0) return undefined;
    const timer = setTimeout(() => setResendLeft((r) => r - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendLeft]);

  if (!settingsReady) {
    return <div className="max-w-lg mx-auto px-4 py-16 text-center text-ink-faint">{te ? 'లోడ్ అవుతున్నది…' : 'Loading…'}</div>;
  }

  const exitCheckout = () => navigate('/cart');

  // ── Auth handlers ─────────────────────────────────────────────
  const handleSendOtp = async () => {
    setAuthError('');
    const digits = authPhone.replace(/\D/g, '');
    if (!authName.trim()) { setAuthError('Please enter your name'); return; }
    if (digits.length < 10) { setAuthError('Enter a valid 10-digit mobile number'); return; }
    setAuthLoading(true);
    try {
      const refCode = (() => { try { return sessionStorage.getItem('nrk_ref') || ''; } catch { return ''; } })();
      const res = await axios.post(`${API}/api/auth/send-otp`, { phone: digits, name: authName.trim(), email: authEmail.trim(), referral_code: refCode || undefined });
      setNormPhone(res.data.phone);
      if (res.data.otp_required === false) { await handleDirectSignup(res.data.phone); return; }
      setAuthStep('otp'); setResendLeft(30);
      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Failed to send OTP. Try again.');
    } finally { setAuthLoading(false); }
  };

  const handleDirectSignup = async (normalizedPhone) => {
    try {
      const res = await axios.post(`${API}/api/auth/verify-otp`, { phone: normalizedPhone, otp: '000000', name: authName.trim(), email: authEmail.trim() });
      const loggedUser = { ...(res.data.user || {}), token: res.data.token };
      try { sessionStorage.removeItem('nrk_ref'); } catch { /* ignore */ }
      onAuth(loggedUser);
      setPhoneVal(normalizedPhone.replace('+91', ''));
      setNameVal(authName.trim() || loggedUser?.name || '');
      await loadAddresses(loggedUser?.phone || normalizedPhone, loggedUser?.token);
      setStep('cart');
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Signup failed. Try again.');
    } finally { setAuthLoading(false); }
  };

  const handleOtpChange = (i, val) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...authOtp]; next[i] = d; setAuthOtp(next);
    if (d && i < 5) setTimeout(() => otpRefs.current[i + 1]?.focus(), 0);
    if (d && next.every((x) => x)) handleVerifyOtp(next.join(''));
  };

  const handleOtpKey = (i, e) => {
    if (e.key === 'Backspace' && !authOtp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const handleVerifyOtp = async (code) => {
    const otpStr = code || authOtp.join('');
    if (otpStr.length < 6) { setAuthError('Enter all 6 digits'); return; }
    setAuthError(''); setAuthLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/verify-otp`, { phone: normPhone, otp: otpStr, name: authName.trim(), email: authEmail.trim() });
      const loggedUser = { ...(res.data.user || {}), token: res.data.token };
      try { sessionStorage.removeItem('nrk_ref'); } catch { /* ignore */ }
      onAuth(loggedUser);
      setPhoneVal(normPhone.replace('+91', ''));
      setNameVal(authName.trim() || loggedUser?.name || '');
      await loadAddresses(loggedUser?.phone || normPhone, loggedUser?.token);
      setStep('cart');
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Invalid OTP. Try again.');
      setAuthOtp(['', '', '', '', '', '']);
      setTimeout(() => otpRefs.current[0]?.focus(), 0);
    } finally { setAuthLoading(false); }
  };

  const handleResend = async () => {
    if (resendLeft > 0) return;
    setAuthError(''); setAuthOtp(['', '', '', '', '', '']); setAuthLoading(true);
    try {
      await axios.post(`${API}/api/auth/send-otp`, { phone: normPhone, name: authName.trim(), email: authEmail.trim() });
      setResendLeft(30);
      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch (err) { setAuthError(err.response?.data?.error || 'Failed to resend.'); }
    finally { setAuthLoading(false); }
  };

  const handleApplyPromo = async (forcedCode) => {
    const code = String(forcedCode || promoCode).trim().toUpperCase();
    if (!code) { setPromoError(L('Enter a promo code', 'ప్రోమో కోడ్ నమోదు చేయండి')); return; }
    setPromoCode(code);
    setPromoLoading(true); setPromoError('');
    try {
      const quote = await fetchPricingPreview({ promoCode: code, rethrow: true });
      if (quote && (quote.promo_discount > 0 || quote.promo_code)) {
        setPromoResult({ discount: quote.promo_discount || 0, desc: quote.promo_desc || '', promo_discount: quote.promo_discount || 0 });
        setPromoCode(quote.promo_code || code);
      } else {
        setPromoResult(null);
        setPromoError(L('Invalid promo code', 'చెల్లుబాటు కాని ప్రోమో కోడ్'));
        await fetchPricingPreview({ promoCode: '' });
      }
    } catch (err) {
      setPromoResult(null);
      setPromoError(err.response?.data?.error || err.message || L('Could not verify promo code', 'ప్రోమో కోడ్ ధృవీకరించలేకపోయింది'));
      await fetchPricingPreview({ promoCode: '' });
    } finally { setPromoLoading(false); }
  };

  const removePromo = async () => {
    setPromoResult(null); setPromoCode(''); setPromoError('');
    await fetchPricingPreview({ promoCode: '' });
  };

  const openMapPicker = () => {
    if (!navigator.geolocation) { setLocState('error'); return; }
    setLocState('getting');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: la, longitude: lo } = pos.coords;
        let label = `${la.toFixed(4)}, ${lo.toFixed(4)}`;
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${la}&lon=${lo}&format=json`, { headers: { 'Accept-Language': 'en' } });
          const d = await r.json();
          if (d.display_name) label = d.display_name.split(',').slice(0, 4).join(', ');
        } catch { /* ignore */ }
        setSelectedAddressId(null);
        setLocation({ lat: la, lng: lo, label, address_id: null });
        setLocState('got');
      },
      () => setLocState('denied'),
      { timeout: 12000, enableHighAccuracy: true },
    );
  };

  const submitOrder = async (method) => {
    setError('');
    const cleanPhone = phoneVal.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 10) { setError(L('Enter valid 10-digit phone number', 'సరైన 10-అంకెల ఫోన్ నంబర్')); return null; }
    if (!selectedAddressId && !location) { setError(L('Please select a delivery address', 'డెలివరీ చిరునామా ఎంచుకోండి')); return null; }
    setLoading(true);
    try {
      const fullPhone = `+91${cleanPhone.slice(-10)}`;
      const accountPhone = sessionUser?.phone || fullPhone;
      const quote = await fetchPricingPreview({ payMethod: method });
      const wallet_amount = method === 'wallet' ? (quote?.wallet_amount ?? quote?.total_amount) : undefined;
      const res = await axios.post(`${API}/api/orders`, {
        phone: fullPhone,
        user_phone: accountPhone,
        name: nameVal.trim(),
        items: cart,
        total_amount: parseFloat(total.toFixed(2)),
        notes: notes.trim(),
        delivery_lat: location?.lat || null,
        delivery_lng: location?.lng || null,
        delivery_label: location?.label || null,
        address_id: selectedAddressId || location?.address_id || undefined,
        payment_method: method,
        email: authEmail || sessionUser?.email || '',
        delivery_type: deliveryType,
        promo_code: promoResult ? promoCode.trim().toUpperCase() : undefined,
        wallet_amount,
      }, { headers: authHeaders() });
      return { ...res.data, fullPhone };
    } catch (err) {
      const d = err.response?.data;
      setError(d?.detail || d?.error || L('Failed to place order. Try again.', 'ఆర్డర్ విఫలమైంది. మళ్ళీ ప్రయత్నించండి.'));
      return null;
    } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (payMethod === 'upi') {
      setError('');
      if (!selectedAddressId && !location) { setError(L('Please select a delivery address', 'డెలివరీ చిరునామా ఎంచుకోండి')); return; }
      const cleanPhone = phoneVal.replace(/\D/g, '');
      if (!cleanPhone || cleanPhone.length < 10) { setError(L('Enter valid 10-digit phone number', 'సరైన 10-అంకెల ఫోన్ నంబర్')); return; }
      setOrderResult({ pending: true, amount_due: pricingPreview?.amount_due ?? pricingPreview?.total_amount ?? total, total_amount: pricingPreview?.total_amount ?? total });
      setStep('upi');
      return;
    }

    if (payMethod === 'cashfree') {
      const result = await submitOrder('cashfree');
      if (!result) return;
      const amountDue = Number(result.amount_due ?? result.total_amount ?? 0);
      if (amountDue <= 0) { clearCart(); finishCheckout(result, nameVal.trim(), result.fullPhone); return; }
      try {
        setLoading(true);
        const sess = await axios.post(`${API}/api/payments/cashfree/session`, { order_id: result.order_id }, { headers: authHeaders() });
        if (sess.data?.already_paid) { clearCart(); finishCheckout(result, nameVal.trim(), result.fullPhone); return; }
        setOrderResult({ ...result, ...sess.data, amount_due: amountDue });
        try {
          sessionStorage.setItem('nrk_cf_pending', JSON.stringify({ order_id: result.order_id, order_number: result.order_number, fullPhone: result.fullPhone, name: nameVal.trim(), amount_due: amountDue }));
        } catch { /* ignore */ }
        setStep('cashfree');
      } catch (err) {
        const d = err.response?.data;
        setError(d?.detail || d?.error || L('Could not start payment', 'చెల్లింపు ప్రారంభించలేకపోయాం'));
      } finally { setLoading(false); }
      return;
    }

    if (payMethod === 'razorpay') {
      const token = sessionUser?.token || secureStorage.get('nrk_user')?.token;
      if (!token) { setError(L('Please sign in again to pay online', 'ఆన్‌లైన్ చెల్లింపుకు మళ్ళీ సైన్ ఇన్ చేయండి')); setStep('auth'); return; }
      setError('');
      if (!selectedAddressId && !location) { setError(L('Please select a delivery address', 'డెలివరీ చిరునామా ఎంచుకోండి')); return; }
      const cleanPhone = phoneVal.replace(/\D/g, '');
      if (!cleanPhone || cleanPhone.length < 10) { setError(L('Enter valid 10-digit phone number', 'సరైన 10-అంకెల ఫోన్ నంబర్')); return; }
      const bundle = await startRazorpayCheckout();
      if (!bundle) return;
      if (bundle.already_paid) { clearCart(); finishCheckout(bundle, nameVal.trim(), bundle.fullPhone); return; }
      setStep('razorpay');
      return;
    }

    const result = await submitOrder('cod');
    if (!result) return;
    clearCart(); finishCheckout(result, nameVal.trim(), result.fullPhone);
  };

  const refreshCashfreeSession = async () => {
    if (!orderResult?.order_id) return null;
    try {
      setLoading(true); setError('');
      const sess = await axios.post(`${API}/api/payments/cashfree/session`, { order_id: orderResult.order_id }, { headers: authHeaders() });
      if (sess.data?.already_paid) { clearCart(); finishCheckout(orderResult, nameVal.trim(), orderResult.fullPhone); return null; }
      const next = { ...orderResult, ...sess.data };
      setOrderResult(next);
      return next;
    } catch (err) {
      const d = err.response?.data;
      setError(d?.detail || d?.error || L('Could not restart payment', 'చెల్లింపు మళ్ళీ ప్రారంభించలేకపోయాం'));
      return null;
    } finally { setLoading(false); }
  };

  // New flow: no NRK order exists until payment is verified. This builds
  // one checkout session (a Razorpay order id, not an NRK order) off the
  // current cart/address/promo state — used both to start paying and, if
  // the session goes stale, to transparently issue a fresh one.
  const buildRazorpayPayload = () => {
    const cleanPhone = phoneVal.replace(/\D/g, '');
    const fullPhone = `+91${cleanPhone.slice(-10)}`;
    const accountPhone = sessionUser?.phone || fullPhone;
    return {
      phone: fullPhone,
      user_phone: accountPhone,
      name: nameVal.trim(),
      items: cart,
      notes: notes.trim(),
      delivery_lat: location?.lat || null,
      delivery_lng: location?.lng || null,
      delivery_label: location?.label || null,
      address_id: selectedAddressId || location?.address_id || undefined,
      payment_method: 'razorpay',
      email: authEmail || sessionUser?.email || '',
      delivery_type: deliveryType,
      promo_code: promoResult ? promoCode.trim().toUpperCase() : undefined,
    };
  };

  const startRazorpayCheckout = async () => {
    const cleanPhone = phoneVal.replace(/\D/g, '');
    const fullPhone = `+91${cleanPhone.slice(-10)}`;
    const token = sessionUser?.token || secureStorage.get('nrk_user')?.token;
    try {
      setLoading(true); setError('');
      const sess = await axios.post(`${API}/api/payments/razorpay/checkout`, buildRazorpayPayload(), { headers: authHeaders() });
      if (sess.data?.already_paid) {
        return { ...sess.data, fullPhone };
      }
      const bundle = { ...sess.data, fullPhone, createdAt: Date.now(), authToken: token };
      setOrderResult(bundle);
      try {
        sessionStorage.setItem('nrk_rzp_pending', JSON.stringify({
          razorpay_order_id: sess.data.razorpay_order_id, fullPhone, name: nameVal.trim(),
          amount_due: sess.data.amount, createdAt: bundle.createdAt,
        }));
      } catch { /* ignore */ }
      return bundle;
    } catch (err) {
      const d = err.response?.data;
      setError(d?.detail || d?.error || L('Could not start payment', 'చెల్లింపు ప్రారంభించలేకపోయాం'));
      return null;
    } finally { setLoading(false); }
  };

  const refreshRazorpaySession = () => startRazorpayCheckout();

  const handleCashfreePaid = (paidResult) => {
    clearCart();
    try { sessionStorage.removeItem('nrk_cf_pending'); } catch { /* ignore */ }
    finishCheckout(paidResult || orderResult, nameVal.trim(), (paidResult || orderResult)?.fullPhone);
  };

  const handleRazorpayPaid = (paidResult) => {
    clearCart();
    try { sessionStorage.removeItem('nrk_rzp_pending'); } catch { /* ignore */ }
    finishCheckout(paidResult || orderResult, nameVal.trim(), (paidResult || orderResult)?.fullPhone);
  };

  // No order exists yet for a razorpay attempt — cancelling just drops the
  // checkout session and returns to the payment step with cart untouched,
  // instead of the old behaviour of stashing an "unpaid" order.
  const handleRazorpayCancel = () => {
    setOrderResult(null);
    setError('');
    try { sessionStorage.removeItem('nrk_rzp_pending'); } catch { /* ignore */ }
    setStep('payment');
  };

  const handleCashfreeBack = () => setStep('payment');

  const handleUpiDone = async () => {
    if (orderResult && !orderResult.pending && orderResult.order_number) {
      clearCart();
      finishCheckout(orderResult, nameVal.trim(), `+91${phoneVal.replace(/\D/g, '').slice(-10)}`);
      return;
    }
    const result = await submitOrder('upi');
    if (!result) return;
    clearCart();
    finishCheckout(result, nameVal.trim(), result.fullPhone);
  };

  const handleUpiBack = () => { setOrderResult(null); setStep('payment'); };

  const handleCopyUpi = () => {
    const pa = upiSettings.upi_pa || '';
    navigator.clipboard.writeText(pa).then(() => { setCopied(true); setTimeout(() => setCopied(false), 3000); }).catch(() => {
      const el = document.createElement('input');
      el.value = pa; document.body.appendChild(el);
      el.select(); document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true); setTimeout(() => setCopied(false), 3000);
    });
  };

  const continueFromAddress = () => {
    setError('');
    if (!selectedAddressId && !location) { setError(L('Please select a delivery address', 'డెలివరీ చిరునామా ఎంచుకోండి')); return; }
    setStep('payment');
  };

  const wrap = (children) => <div className="max-w-lg mx-auto px-4 md:px-6 py-5 md:py-8">{children}</div>;

  if (step === 'auth') return wrap(
    <AuthStep
      authStep={authStep} authName={authName} setAuthName={setAuthName}
      authPhone={authPhone} setAuthPhone={setAuthPhone}
      authEmail={authEmail} setAuthEmail={setAuthEmail}
      consent={consent} setConsent={setConsent}
      authOtp={authOtp} otpRefs={otpRefs} normPhone={normPhone}
      authLoading={authLoading} authError={authError} resendLeft={resendLeft}
      upiSettings={upiSettings} cart={cart} total={total} te={te} L={L}
      handleSendOtp={handleSendOtp} handleOtpChange={handleOtpChange}
      handleOtpKey={handleOtpKey} handleVerifyOtp={handleVerifyOtp}
      handleResend={handleResend} onClose={exitCheckout} authOnly={false}
      onChangeNumber={() => { setAuthStep('form'); setAuthOtp(['', '', '', '', '', '']); }}
    />,
  );

  if (step === 'cart') return wrap(
    <CartStep cart={cart} total={total} te={te} L={L} t={t} onClose={exitCheckout} onContinue={() => setStep('address')} />,
  );

  if (step === 'address') return wrap(
    <AddressStep
      nameVal={nameVal} setNameVal={setNameVal}
      phoneVal={phoneVal} setPhoneVal={setPhoneVal}
      savedAddresses={savedAddresses}
      selectedAddressId={selectedAddressId} setSelectedAddressId={setSelectedAddressId}
      location={location} setLocation={setLocation}
      locState={locState} setLocState={setLocState}
      openMapPicker={openMapPicker}
      error={error} te={te} L={L}
      onClose={exitCheckout} onBack={() => setStep('cart')}
      onContinue={continueFromAddress}
    />,
  );

  if (step === 'payment' || step === 'checkout') return wrap(
    <CheckoutStep
      notes={notes} setNotes={setNotes}
      loading={loading} error={error}
      upiSettings={upiSettings}
      deliveryType={deliveryType} setDeliveryType={setDeliveryType}
      immediateCharge={immediateCharge}
      walletBalance={walletBalance}
      payMethod={payMethod} setPayMethod={setPayMethod}
      promoCode={promoCode} setPromoCode={(v) => { setPromoCode(v); setPromoError(''); }}
      promoResult={promoResult} promoLoading={promoLoading} promoError={promoError}
      availablePromos={availablePromos}
      cart={cart} total={total} te={te} L={L} t={t}
      pricingPreview={pricingPreview}
      selectedAddressLabel={location?.label}
      handleSubmit={handleSubmit}
      handleApplyPromo={() => handleApplyPromo()}
      onSelectPromo={(code) => handleApplyPromo(code)}
      removePromo={removePromo}
      onClose={exitCheckout} onBack={() => setStep('address')} onSupportOpen={() => setSupportOpen(true)}
    />,
  );

  if (step === 'upi') return wrap(
    <UpiStep orderResult={orderResult} total={total} upiSettings={upiSettings} copied={copied} te={te} L={L}
      loading={loading} error={error} handleCopyUpi={handleCopyUpi} handleUpiDone={handleUpiDone} handleUpiBack={handleUpiBack} />,
  );

  if (step === 'cashfree') return wrap(
    <CashfreePayStep orderResult={orderResult} te={te} L={L} loading={loading} error={error}
      onPaid={handleCashfreePaid} onBack={handleCashfreeBack} onRetrySession={refreshCashfreeSession} onSupportOpen={() => setSupportOpen(true)} />,
  );

  if (step === 'razorpay') return wrap(
    <RazorpayPayStep orderResult={orderResult} te={te} L={L} loading={loading} error={error}
      onPaid={handleRazorpayPaid} onBack={handleRazorpayCancel}
      onRetrySession={refreshRazorpaySession} onSupportOpen={() => setSupportOpen(true)} />,
  );

  return null;
}
